import React from 'react';
import { Dialog, DialogBaseExtendProps } from 'components/_new/Dialog';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from 'components/_new/Spinner';
import { Table } from 'components/_new/Table/Table';
import { userDisplayName } from 'utils/userDisplayName';
import { dateFormat } from 'utils/dateFormat';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import { Link } from 'components/_new/Link';
import { Avatar } from 'components/_new/Avatar';
import { classNames } from 'primereact/utils';
import { Icon } from 'components/_new/Icon';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { KnowledgeBaseArticleVisitDetails } from 'types/KnowledgeBase';
import { useScope } from 'contexts/scopeContext';
import { useKnowledgeBase } from 'components/knowledgeBase/knowledgeBaseContext';

type KnowledgeBaseArticleVisitDetailsWithKey = KnowledgeBaseArticleVisitDetails & {
	key: string;
};

type VisitsDetailsDialogProps = DialogBaseExtendProps & {
	articleId: number;
};

export const VisitsDetailsDialog = ({ articleId, ...dialogProps }: VisitsDetailsDialogProps) => {
	const { t } = useTranslation();

	const { currentProgram: program } = useScope();
	const { queries, context } = useKnowledgeBase();

	const {
		data: visitsDetails,
		isLoading,
		isSuccess,
	} = useQuery(
		[
			'KNOWLEDGE_BASE_PP_ARTICLE_VISITS_DETAILS',
			{
				programId: program?.id,
				articleId,
				context,
			},
		],
		// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain, @typescript-eslint/no-non-null-assertion
		() => queries.getProgramArticleVisitsDetails?.(Number(articleId))!,
		{ enabled: Boolean(articleId) && dialogProps.visible }
	);

	const visitsDetailsData: KnowledgeBaseArticleVisitDetailsWithKey[] = (visitsDetails || []).map((data) => {
		return {
			...data,
			key: `${data.application.id}-${data.application.applicationRole}`,
		};
	});

	return (
		<Dialog title={t('programPanel.knowledge.views')} size="lg" {...dialogProps}>
			{isLoading && <Spinner center />}
			{isSuccess && !isLoading && (
				<Table<KnowledgeBaseArticleVisitDetailsWithKey>
					data={visitsDetailsData}
					keyOptionProp="key"
					columns={[
						{
							label: t('programPanel.application.applicationRole'),
							name: 'role',
							body: ({ application }) => t(`misc.${application.applicationRole}`),
						},
						{
							label: t('programPanel.application.name'),
							name: 'name',
							body: ({ application }) => {
								return (
									<>
										<Tooltip target={`#application-${application.id}`} position="bottom" />
										<Link
											id={`application-${application.id}`}
											to={`/program/${program?.name}/application/${application.id}`}
											className="w-full"
											contentClassName="flex flex-row gap-2 flex-nowrap justify-items-start align-items-center"
											data-pr-tooltip={
												application.deletedAt
													? t('programPanel.application.willBeDeleted', {
															date: dateFormat(application.deletedAt, 'date'),
													  })
													: undefined
											}
										>
											<Avatar
												src={application.programMembership.user.avatar}
												size="md"
												name={userDisplayName(application.programMembership.user, null)}
											/>
											<span className={classNames({ 'text-error': application.deletedAt })}>
												{userDisplayName(application.programMembership.user)}
											</span>
										</Link>
									</>
								);
							},
						},
						{ label: t('programPanel.knowledge.views'), name: 'visits' },
						{
							label: t('programPanel.knowledge.firstView'),
							name: 'firstView',
							body: ({ firstVisitAt }) => dateFormat(firstVisitAt, 'full'),
						},
						{
							label: t('programPanel.knowledge.ratings'),
							name: 'rating',
							body: ({ application, applicationRating }) => {
								if (applicationRating) {
									const hasComment =
										applicationRating?.comment && String(applicationRating.comment).length > 0;
									return (
										<>
											<Tooltip
												target={`#rating-${application.id}`}
												position="bottom"
												disabled={!hasComment}
											/>
											<div
												id={`rating-${application.id}`}
												data-pr-tooltip={applicationRating.comment}
												className="relative inline-block"
											>
												{applicationRating.rating === 1 && (
													<Icon
														name="thumbs-up"
														iconStyle="regular"
														className="text-success text-xl"
													/>
												)}
												{applicationRating.rating === -1 && (
													<Icon
														name="thumbs-down"
														iconStyle="regular"
														className="w-full text-error text-xl"
													/>
												)}
												{hasComment && (
													<Icon
														name="comment-dots"
														iconStyle="solid"
														className="absolute text-muted text-xs"
														style={{ bottom: -6, right: -12 }}
													/>
												)}
											</div>
										</>
									);
								}
								return <span>-</span>;
							},
						},
					]}
				/>
			)}
		</Dialog>
	);
};
